import { SettingsSubModule } from '../utils/settings-sub-module';
import { SettingValue } from '../utils/setting-value';
import { SettingsModule } from '../utils/settings-module';
import { DocumentSecurityLevel, eSignatureVendor } from '../../../origami-api/constants';
import { TiFeatures } from '@app/origami-api/constants/features';

export namespace SecurityFields {
  export enum ResetPasswordField {
    MinutesToExpiration = 'ResetPassword.MinutesToExpiration',
    EnablePasswordRestore = 'ResetPassword.FeatureOn',
    RequiredQuestionsNumber = 'ResetPassword.RequiredQuestions',
    LinkExpirationPeriod = 'ResetPassword.LinkExpiredInMinutes',
  }

  export enum PasswordExpirationField {
    PasswordExpireAfter = 'PasswordExpiration.PasswordExpireAfter',
    RemindBeforePasswordExpires = 'PasswordExpiration.RemindBeforePasswordExpires',
  }

  export enum AccountLockingField {
    AllowedLoginAttemptsCount = 'AccountLocking.AllowedLoginAttemptsCount',
    LockingTime = 'AccountLocking.LockingTime',
  }

  export enum IdleTimerField {
    TimeToIdle = 'IdleTimer.TimeToIdle',
    SecondsToAprove = 'IdleTimer.SecondsToAprove',
    AlertText = 'IdleTimer.AlertText',
    ConfirmationText = 'IdleTimer.ConfirmationText',
  }

  export enum WatermarkField {
    UseWatermark = 'Watermark.UseWatermark',
    UseWatermarkForNonPDFDocuments = 'Watermark.UseWatermarkForNonPDFDocuments',
    Text = 'Watermark.Text',
    AllPrintable = 'Watermark.AllPrintable',
    NonFinalOnly = 'Watermark.NonFinalOnly',
    FontName = 'Watermark.FontName',
    FontBold = 'Watermark.FontBold',
    FontItalic = 'Watermark.FontItalic',
    FontSize = 'Watermark.FontSize',
    FontColor = 'Watermark.FontColor',
    Position = 'Watermark.Position',
    Visibility = 'Watermark.Visibility',
    EmbedFont = 'Watermark.EmbedFont',
    CheckPageRotation = 'Watermark.CheckPageRotation',
  }

  export enum EncryptionField {
    AllowFullPrintForNonAdmins = 'Encryption.AllowFullPrintForNonAdmins',
    ExtensionsToEncrypt = 'Encryption.ExtensionsToEncrypt',
    EnableDRM = 'Encryption.EnableDrm',
    EdrmService = 'Encryption.EdrmService',
  }

  export enum PinField {
    UsePin = 'Pin.UsePin',
    Value = 'Pin.Value',
  }

  export enum RedactionField {
    UseRedaction = 'Redaction.UseRedaction',
  }

  export enum EsignatureField {
    UseEsignature = 'Esignature.UseEsignature',
    AvailableProviders = 'Esignature.AvailableProviders',
    Purpose = 'Esignature.Purpose',
    EnableSignerRole = 'Esignature.EnableSignerRole',
    AdobeSignStoreCertInDoc = 'Esignature.AdobeSignStoreCertInDoc',
    DocuSignIsCfrPart11 = 'Esignature.DocuSignIsCfrPart11',
    DocuSignCertInDoc = 'Esignature.DocuSignCertInDoc',
  }

  export enum ViewerField {
    Type = 'Viewer.Type',
    AvailableViewers = 'Viewer.AvailableViewers',
    SingleViewer = 'Viewer.SingleViewer',
    FlashViewerUseCache = 'Viewer.FlashViewerUseCache',
  }

  export enum SecurityLevelField {
    Level = 'SecurityLevel.Target',
  }
}

class ResetPasswordConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.ResetPasswordField.MinutesToExpiration, 60)
  minutesToExpiration: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.ResetPasswordField.EnablePasswordRestore, 0)
  enablePasswordRestore: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.ResetPasswordField.RequiredQuestionsNumber, 3)
  requiredQuestionsNumber: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.ResetPasswordField.LinkExpirationPeriod, 120)
  linkExpirationPeriod: SettingValue<number>;
}

class PasswordExpirationConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.PasswordExpirationField.PasswordExpireAfter, 90)
  passwordExpireAfter: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.PasswordExpirationField.RemindBeforePasswordExpires, '3')
  remindBeforePasswordExpires: SettingValue<string>;
}

class AccountLockingConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.AccountLockingField.AllowedLoginAttemptsCount, 3)
  allowedLoginAttemptsCount: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.AccountLockingField.LockingTime, 1) lockingTime: SettingValue<number>;
}

class IdleTimerConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.IdleTimerField.TimeToIdle, '99:99', {
    getterConverter(value: string): any {
      const arr = value.split(':');

      if (arr.length > 1) {
        return (+arr[0] * 60 + +arr[1]) * 1000;
      }
    },
  })
  timeToIdle: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.IdleTimerField.SecondsToAprove, 99) secondsToAprove: SettingValue<number>;
  @SettingsSubModule.value(
    SecurityFields.IdleTimerField.AlertText,
    'Your session expired and you were automatically signed out.',
  )
  alertText: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.IdleTimerField.ConfirmationText, 'Do you want to sign out?')
  confirmationText: SettingValue<string>;
}

class WatermarkConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.WatermarkField.UseWatermark, false) useWatermark: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.UseWatermarkForNonPDFDocuments, false)
  useWatermarkForNonPDFDocuments: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.Text, '') text: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.AllPrintable, false) allPrintable: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.NonFinalOnly, false) nonFinalOnly: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.FontName, '') fontName: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.FontBold, false) fontBold: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.FontItalic, false) fontItalic: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.FontSize, 10) fontSize: SettingValue<number>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.FontColor, '') fontColor: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.Position, '') position: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.Visibility, '') visibility: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.EmbedFont, false) embedFont: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.WatermarkField.CheckPageRotation, false)
  checkPageRotation: SettingValue<boolean>;
}

class EncryptionConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.EncryptionField.AllowFullPrintForNonAdmins, false)
  allowFullPrintForNonAdmins: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.EncryptionField.ExtensionsToEncrypt, '')
  extensionsToEncrypt: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.EncryptionField.EnableDRM, false) enableDRM: SettingValue<boolean>;
  // 0: TI, 1: Seclore
  @SettingsSubModule.value(SecurityFields.EncryptionField.EdrmService, 0) edrmService: SettingValue<number>;
}

class PinConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.PinField.UsePin, false) usePin: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.PinField.Value, '') pinValue: SettingValue<string>;
}

class RedactionConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.RedactionField.UseRedaction, false) useRedaction: SettingValue<boolean>;
}

class EsignatureConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.EsignatureField.UseEsignature, eSignatureVendor.none, {
    getterConverter(value: string): number[] {
      if (value === null || value === undefined) {
        return [];
      }
      return value.split(',').map((v) => +v);
    },
  })
  useEsignature: SettingValue<eSignatureVendor[]>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.AvailableProviders, eSignatureVendor.none, {
    getterConverter(value: string): number[] {
      if (value === null || value === undefined) {
        return [];
      }
      return value.split(',').map((v) => +v);
    },
  })
  availableProviders: SettingValue<eSignatureVendor[]>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.Purpose, '') purpose: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.EnableSignerRole, false)
  enableSignerRole: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.AdobeSignStoreCertInDoc, false)
  adobeSignStoreCertInDoc: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.DocuSignCertInDoc, false)
  docuSignStoreCertInDoc: SettingValue<boolean>;
  @SettingsSubModule.value(SecurityFields.EsignatureField.DocuSignIsCfrPart11, false)
  docuSignIsCfrPart11: SettingValue<boolean>;
}

class ViewerConfig extends SettingsSubModule {
  @SettingsSubModule.value(SecurityFields.ViewerField.Type, TiFeatures.Original) type: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.ViewerField.AvailableViewers, TiFeatures.Original)
  availableViewers: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.ViewerField.SingleViewer, '') singleViewer: SettingValue<string>;
  @SettingsSubModule.value(SecurityFields.ViewerField.FlashViewerUseCache, false)
  flashViewerUseCache: SettingValue<boolean>;
}

class SecurityLevelConfig extends SettingsSubModule {
  // must be: folder, document
  @SettingsSubModule.value(SecurityFields.SecurityLevelField.Level, DocumentSecurityLevel.Document)
  level: SettingValue<DocumentSecurityLevel>;
}

export class SecurityModule extends SettingsModule {
  @SettingsModule.value(ResetPasswordConfig) resetPassword: ResetPasswordConfig;
  @SettingsModule.value(PasswordExpirationConfig) passwordExpiration: PasswordExpirationConfig;
  @SettingsModule.value(AccountLockingConfig) accountLocking: AccountLockingConfig;
  @SettingsModule.value(IdleTimerConfig) idleTimer: IdleTimerConfig;
  @SettingsModule.value(WatermarkConfig) watermark: WatermarkConfig;
  @SettingsModule.value(EncryptionConfig) encryption: EncryptionConfig;
  @SettingsModule.value(PinConfig) pin: PinConfig;
  @SettingsModule.value(RedactionConfig) redaction: RedactionConfig;
  @SettingsModule.value(EsignatureConfig) esignature: EsignatureConfig;
  @SettingsModule.value(ViewerConfig) viewer: ViewerConfig;
  @SettingsModule.value(SecurityLevelConfig) securityLevel: SecurityLevelConfig;
}
